<template>
    <div v-if="show" class="premium-paywall">
        <div class="premium-paywall__overlay" @click="close"></div>
        <div
            class="premium-paywall__card"
            :class="{
                'premium-paywall__card--lesson-record': isLessonRecord,
            }"
            @click.stop
        >
            <button class="premium-paywall__close" type="button" aria-label="Close" @click="close">
                <x-icon class="premium-paywall__close-icon" />
            </button>

            <h2 class="premium-paywall__title">{{ title }}</h2>
            <p v-if="isLessonRecord" class="premium-paywall__subtitle">
                Upgrade to Premium and unlock your lesson records to review any time
            </p>
            <p v-else class="premium-paywall__subtitle">
                Unlock your lesson records<br />
                and unlock your path to better golf
            </p>

            <div class="premium-paywall__benefits">
                <ul class="premium-paywall__benefits-list">
                    <li v-for="benefit in benefits" :key="benefit" class="premium-paywall__benefit">
                        <img
                            src="@/assets/icons/star-fill.svg"
                            alt=""
                            class="premium-paywall__benefit-icon"
                        />
                        <span>{{ benefit }}</span>
                    </li>
                </ul>
            </div>

            <p class="premium-paywall__info">
                Lesson records are stored for free for up to {{ lessonRecordFreeAccessDays }} days
                with a standard account. To continue accessing your records after
                {{ lessonRecordFreeAccessDays }} days subscribe to a premium account.
            </p>

            <div v-if="storeUpdateRequired" class="premium-paywall__store-update">
                <p class="premium-paywall__web-fallback">
                    Premium subscriptions are only available in the latest version of the Lesson Pro
                    app. Please update from the App Store or Google Play to upgrade.
                </p>

                <button
                    v-for="link in storeLinks"
                    :key="link.url"
                    type="button"
                    class="premium-paywall__cta"
                    @click="openExternalTab(link.url)"
                >
                    {{ link.label }}
                </button>
            </div>

            <template v-else>
                <div class="premium-paywall__pricing">
                    <template v-if="plansReady">
                        <button
                            v-for="plan in displayPlans"
                            :key="plan.key"
                            type="button"
                            class="premium-paywall__plan"
                            :class="{
                                'premium-paywall__plan--selected': selectedPlan === plan.key,
                                'premium-paywall__plan--annual': plan.isAnnual,
                            }"
                            @click="selectedPlan = plan.key"
                        >
                            <span class="premium-paywall__plan-label">{{ plan.label }}</span>
                            <span class="premium-paywall__plan-price">{{ plan.price }}</span>
                            <!-- <img src="@/assets/arrow-right-white.svg" alt="" class="premium-paywall__plan-arrow" /> -->
                        </button>
                    </template>
                    <template v-else>
                        <!-- Skeleton placeholders shown until plans load from the API -->
                        <div
                            class="premium-paywall__plan premium-paywall__plan--skeleton"
                            aria-hidden="true"
                        ></div>
                        <div
                            class="premium-paywall__plan premium-paywall__plan--skeleton"
                            aria-hidden="true"
                        ></div>
                    </template>
                </div>

                <p v-if="errorMessage" class="premium-paywall__error">{{ errorMessage }}</p>

                <button
                    type="button"
                    class="premium-paywall__cta"
                    :disabled="busy || !canPurchase"
                    @click="subscribe"
                >
                    {{ ctaLabel }}
                </button>

                <p class="premium-paywall__terms">
                    By proceeding you agree to our
                    <a
                        href="https://lessonpro.co.uk/terms"
                        @click.prevent="openExternalTab('https://lessonpro.co.uk/terms')"
                        >Terms</a
                    >.
                </p>
            </template>
        </div>
    </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { XIcon } from "vue-feather-icons";
import { LESSON_RECORD_FREE_ACCESS_DAYS } from "@/mixins/lesson-record-access-helpers";

let cachedSubscriptionPlans = [];

const APP_STORE_URL = "https://apps.apple.com/gb/app/lesson-pro/id1550581581";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.lessonpro.app";
const PRODUCTS_TIMEOUT_MS = 8000;

export default {
    name: "premium-paywall",
    components: { XIcon },
    created() {
        if (this.isLoggedIn()) {
            this.prefetchSubscriptionPlans();
        }

        this.$watch(
            () => this.$store.state.user,
            (user) => {
                if (user) {
                    this.prefetchSubscriptionPlans();
                }
            },
        );

        this.premiumPaywallHub.$on("open", (options = {}) => {
            this.variant = options.variant || "premium";
            this.daysRemaining = options.daysRemaining ?? 10;
            this.busy = false;
            this.errorMessage = null;
            this.storeUpdateRequired = false;
            this.hydrateFromCache();
            this.show = true;
            this.requestProducts();
        });

        this.premiumPaywallHub.$on("done", () => {
            this.busy = false;
            this.show = false;
            this.teardownProducts();
        });

        this.premiumPaywallHub.$on("error", (message) => {
            this.busy = false;
            this.errorMessage = message || "Something went wrong. Please try again.";
        });

        this.premiumPaywallHub.$on("requireStoreUpdate", () => {
            this.busy = false;
            this.storeUpdateRequired = true;
        });
    },
    data() {
        return {
            show: false,
            busy: false,
            variant: "premium",
            lessonRecordFreeAccessDays: LESSON_RECORD_FREE_ACCESS_DAYS,
            daysRemaining: 10,
            selectedPlan: null,
            // Active plans from GET /api/subscription/plans (display_price shown in the UI).
            plans: [],
            // Store items returned by the native shell via the 'productsSent' event (carry localized price).
            storeItems: [],
            loadingPlans: false,
            productsReady: false,
            // Web browser or outdated native shell that cannot complete a store purchase.
            storeUpdateRequired: false,
            productsTimeoutId: null,
            errorMessage: null,
            backgroundScrollLocked: false,
            savedScrollY: 0,
            benefits: [
                "Rewatch lesson videos anytime",
                "Review before you practice",
                "All coaching notes in one place",
                "Track your improvement",
                "Get more value from your lessons",
            ],
        };
    },
    computed: {
        isLessonRecord() {
            return this.variant === "lessonRecord";
        },
        title() {
            if (this.isLessonRecord) {
                const days = this.daysRemaining;

                if (days === 1) {
                    return "1 day to go";
                }

                return `${days} days to go`;
            }

            return "Go Premium";
        },
        plansReady() {
            return !this.loadingPlans && this.plans.length > 0;
        },
        canPurchase() {
            if (!this.plansReady || !this.productsReady) {
                return false;
            }

            return this.hasMatchingStoreProduct();
        },
        ctaLabel() {
            if (this.busy) {
                return "Please wait...";
            }

            if (this.loadingPlans || (this.plansReady && !this.productsReady)) {
                return "Loading...";
            }

            if (!this.canPurchase) {
                return "Unavailable";
            }

            return "SUBSCRIBE NOW";
        },
        displayPlans() {
            return this.plans.map((plan) => {
                const storeItem = this.matchStoreItem(plan);

                return {
                    key: plan.billing_interval,
                    label: this.intervalLabel(plan.billing_interval),
                    price: this.planPrice(plan),
                    isAnnual: this.isAnnualInterval(plan.billing_interval),
                    product: storeItem,
                    storeItem,
                    raw: plan,
                };
            });
        },
        storeLinks() {
            if (!window.nsWebViewInterface) {
                return [
                    { label: "App Store", url: APP_STORE_URL },
                    { label: "Google Play", url: PLAY_STORE_URL },
                ];
            }

            if (this.isAndroid()) {
                return [{ label: "Update app", url: PLAY_STORE_URL }];
            }

            return [{ label: "Update app", url: APP_STORE_URL }];
        },
    },
    watch: {
        show(isOpen) {
            if (isOpen) {
                this.lockBackgroundScroll();
            } else {
                this.unlockBackgroundScroll();
            }
        },
    },
    beforeDestroy() {
        this.unlockBackgroundScroll();
        this.teardownProducts();
    },
    methods: {
        isLoggedIn() {
            return !!this.$store.state.user;
        },
        close() {
            if (this.busy) {
                return;
            }
            this.show = false;
            this.teardownProducts();
            this.premiumPaywallHub.$emit("close");
        },
        lockBackgroundScroll() {
            if (this.backgroundScrollLocked) {
                return;
            }

            this.backgroundScrollLocked = true;
            this.savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
            document.body.style.overflow = "hidden";
        },
        unlockBackgroundScroll() {
            if (!this.backgroundScrollLocked) {
                return;
            }

            this.backgroundScrollLocked = false;
            document.body.style.overflow = "";
            window.scrollTo(0, this.savedScrollY);
        },
        prefetchSubscriptionPlans() {
            if (
                !this.isLoggedIn() ||
                !window.nsWebViewInterface ||
                cachedSubscriptionPlans.length
            ) {
                return;
            }

            window.axios
                .get("/subscription/plans")
                .then((response) => {
                    cachedSubscriptionPlans = response.data || [];
                })
                .catch(() => {});
        },
        hydrateFromCache() {
            if (cachedSubscriptionPlans.length) {
                this.plans = cachedSubscriptionPlans.map((plan) => ({ ...plan }));
                this.loadingPlans = false;
                this.selectedPlan = this.plans[0].billing_interval;
            } else {
                this.plans = [];
                this.selectedPlan = null;
            }

            const cachedProducts = this.$store.state.subscriptions;

            if (cachedProducts?.length) {
                this.storeItems = [...cachedProducts];
                this.productsReady = true;
            } else {
                this.storeItems = [];
                this.productsReady = false;
            }
        },
        requestProducts() {
            this.errorMessage = null;
            this.clearProductsTimeout();

            // Outside the native shell there is no store billing API to purchase from.
            if (!window.nsWebViewInterface) {
                this.storeUpdateRequired = true;
                this.loadingPlans = false;
                this.productsReady = false;

                return;
            }

            if (!this.isLoggedIn()) {
                this.loadingPlans = false;
                this.errorMessage = "Please log in to view subscription options.";

                return;
            }

            if (!this.plans.length) {
                this.loadingPlans = true;
            }

            window.nsWebViewInterface.on("productsSent", this.onProductsSent);

            window.axios
                .get("/subscription/plans")
                .then((response) => {
                    const plans = response.data || [];

                    cachedSubscriptionPlans = plans;
                    this.plans = plans;
                    this.loadingPlans = false;

                    if (!this.plans.length) {
                        this.productsReady = true;
                        this.errorMessage =
                            "We couldn't load subscription options. Please try again.";

                        return;
                    }

                    if (!this.selectedPlan) {
                        this.selectedPlan = this.plans[0].billing_interval;
                    }

                    const productIds = [
                        ...new Set(
                            this.plans.map((plan) => this.storeProductIdFor(plan)).filter(Boolean),
                        ),
                    ];

                    if (!productIds.length) {
                        this.productsReady = true;
                        this.storeUpdateRequired = true;

                        return;
                    }

                    // Cached store items may already be enough to purchase while we refresh.
                    if (this.hasMatchingStoreProduct()) {
                        this.productsReady = true;
                        this.storeUpdateRequired = false;
                    }

                    this.startProductsTimeout();
                    window.nsWebViewInterface.emit("getProducts", productIds);
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    this.loadingPlans = false;

                    if (!this.plans.length) {
                        this.storeUpdateRequired = true;
                    }
                });
        },
        onProductsSent(items) {
            this.clearProductsTimeout();

            let parsed = items;

            if (typeof parsed === "string") {
                try {
                    parsed = JSON.parse(parsed);
                } catch (error) {
                    parsed = [];
                }
            }

            this.storeItems = parsed || [];
            this.productsReady = true;

            if (this.hasMatchingStoreProduct()) {
                this.storeUpdateRequired = false;
            } else {
                this.storeUpdateRequired = true;
            }
        },
        onProductsTimeout() {
            this.productsTimeoutId = null;
            this.productsReady = true;

            // Keep purchase available if we already have usable cached store products.
            if (!this.hasMatchingStoreProduct()) {
                this.storeUpdateRequired = true;
            }
        },
        startProductsTimeout() {
            this.clearProductsTimeout();
            this.productsTimeoutId = setTimeout(
                () => this.onProductsTimeout(),
                PRODUCTS_TIMEOUT_MS,
            );
        },
        clearProductsTimeout() {
            if (this.productsTimeoutId) {
                clearTimeout(this.productsTimeoutId);
                this.productsTimeoutId = null;
            }
        },
        teardownProducts() {
            this.clearProductsTimeout();

            if (window.nsWebViewInterface) {
                window.nsWebViewInterface.off("productsSent", this.onProductsSent);
            }
        },
        hasMatchingStoreProduct() {
            return this.plans.some((plan) => !!this.matchStoreItem(plan));
        },
        isAndroid() {
            return !!window.androidWebViewInterface;
        },
        storeProductIdFor(plan) {
            return this.isAndroid() ? plan.android_product_id : plan.ios_product_id;
        },
        matchStoreItem(plan) {
            if (!plan) {
                return null;
            }

            if (this.isAndroid() && plan.android_base_plan_id) {
                return (
                    (this.storeItems || []).find((item) => {
                        return item.basePlanId === plan.android_base_plan_id;
                    }) || null
                );
            }

            const productId = this.storeProductIdFor(plan);

            if (!productId) {
                return null;
            }

            return (
                (this.storeItems || []).find((item) => {
                    const id =
                        item.productIdentifier ||
                        item.itemId ||
                        item.productId ||
                        item.product ||
                        item.id ||
                        item.sku;

                    return id === productId;
                }) || null
            );
        },
        planPrice(plan) {
            // Keep Nova display_price in the UI; skip overwriting with App Store / Play Store pricing.
            // const storePrice = this.storeItemPrice(storeItem);
            //
            // if (storePrice) {
            //     return storePrice;
            // }

            return plan.display_price || "";
        },
        // storeItemPrice(item) {
        //     if (!item) {
        //         return "";
        //     }
        //
        //     return item.localizedPrice || item.priceFormatted || item.price || "";
        // },
        intervalLabel(interval) {
            return this.isAnnualInterval(interval) ? "Annual" : "Monthly";
        },
        isAnnualInterval(interval) {
            return interval === "yearly" || interval === "annual";
        },
        subscribe() {
            if (this.busy || !this.canPurchase) {
                return;
            }

            const plan = this.displayPlans.find((item) => item.key === this.selectedPlan);

            if (!plan || !plan.storeItem) {
                this.storeUpdateRequired = true;

                return;
            }

            this.busy = true;
            this.errorMessage = null;
            this.premiumPaywallHub.$emit("subscribe", {
                plan: this.selectedPlan,
                planRow: plan.raw,
                product: plan.product,
                storeItem: plan.storeItem,
                variant: this.variant,
                daysRemaining: this.isLessonRecord ? this.daysRemaining : null,
            });
        },
        openExternalTab(url) {
            if (window.nsWebViewInterface) {
                window.nsWebViewInterface.emit("openExternalTab", url);
            } else {
                window.open(url, "_blank");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.premium-paywall {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 20px);
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2000;
    box-sizing: border-box;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

.premium-paywall__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba($darkGrey_0, 0.9);
    z-index: 2000;
}

.premium-paywall__card {
    position: relative;
    z-index: 2001;
    width: 100%;
    max-width: calc(100% - 60px);
    margin: 0 auto;
    background: $green_0;
    border-radius: $radius;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
    padding: 24px 20px 28px;
    font-family: canada-type-gibson, sans-serif;

    &--lesson-record {
        .premium-paywall__subtitle {
            margin-bottom: 24px;
        }

        .premium-paywall__info {
            margin-bottom: 24px;
        }
    }
}

.premium-paywall__close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: 0;
    padding: 4px;
    cursor: pointer;
    color: white;
    line-height: 0;

    &:focus {
        outline: 0;
    }
}

.premium-paywall__close-icon {
    // width: 18px;
    // height: 18px;
    // color: white;
}

.premium-paywall__title {
    margin: 16px 0 8px;
    font-size: 32px;
    font-weight: normal;
    line-height: 36px;
    text-align: center;
    color: white;
}

.premium-paywall__subtitle {
    margin: 0 0 20px;
    font-size: 22px;
    line-height: 30px;
    text-align: center;
    text-wrap: pretty;
    color: $green_3;
}

.premium-paywall__benefits {
    background: rgba($green_1, 0.6);
    border: 1px solid $green_3;
    border-radius: $radius;
    padding: 12px 16px 12px 12px;
    margin-bottom: 20px;
}

.premium-paywall__benefits-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.premium-paywall__benefit {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 16px;
    line-height: 30px;
    color: white;
}

.premium-paywall__benefit-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 5px;
}

.premium-paywall__info {
    margin: 0 0 20px;
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    color: $green_3;
}

.premium-paywall__pricing {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.premium-paywall__plan {
    flex: 1;
    position: relative;
    display: block;
    text-align: left;
    padding: 14px 36px 14px 14px;
    border-radius: $radius;
    border: 1px solid $grey_mid;
    background: linear-gradient(180deg, $green_0 0%, $darkGrey_0 100%);
    color: white;
    cursor: pointer;
    font-family: canada-type-gibson, sans-serif;
    appearance: none;

    &:focus {
        outline: 0;
    }

    &--selected {
        border-color: $green_3;
        background: linear-gradient(180deg, $green_0 0%, $green_1 100%);
    }

    &--annual:not(.premium-paywall__plan--selected) {
        background: linear-gradient(180deg, $green_0 0%, $darkGrey_0 100%);
    }

    &--skeleton {
        cursor: default;
        height: 70px;
        border-color: $grey_mid;
        background: linear-gradient(180deg, $green_0 0%, $darkGrey_0 100%);
        animation: premium-paywall-pulse 1.4s ease-in-out infinite;
    }
}

@keyframes premium-paywall-pulse {
    0% {
        opacity: 0.55;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0.55;
    }
}

.premium-paywall__error {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 22px;
    text-align: center;
    color: $red;
}

.premium-paywall__store-update {
    .premium-paywall__cta + .premium-paywall__cta {
        margin-top: 10px;
    }
}

.premium-paywall__web-fallback {
    margin: 0 0 20px;
    padding: 16px;
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    color: white;
    background: rgba($green_1, 0.6);
    border: 1px solid $green_3;
    border-radius: $radius;
}

.premium-paywall__plan-label {
    display: block;
    font-size: 16px;
    line-height: 26px;
}

.premium-paywall__plan-price {
    display: block;
    font-size: 28px;
    line-height: 26px;
}

.premium-paywall__plan-arrow {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: auto;
}

.premium-paywall__cta {
    display: block;
    width: 100%;
    height: 52px;
    border: 0;
    border-radius: 100px;
    background: linear-gradient(180deg, $green_0 -51.92%, $green_1 100%);
    box-shadow: 0 4px 10px rgba(46, 58, 55, 0.5);
    color: white;
    font-size: 16px;
    font-weight: 600;
    font-family: canada-type-gibson, sans-serif;
    letter-spacing: 0.02em;
    cursor: pointer;
    margin-bottom: 16px;

    &:focus {
        outline: 0;
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
}

.premium-paywall__terms {
    margin: 0;
    font-size: 14px;
    line-height: 26px;
    text-align: center;
    color: $green_3;

    a {
        color: $green_3;
        text-decoration: underline;
    }
}
</style>
